import { Feature, LICENSE_STATUS, WARNING_FLAGS } from '@/licenseManagement/licenseManagement.constants';
import { ValidityEnum } from '@seeqdev/api/model/LicenseStatusOutputV1';
import type {
  LicenseConfiguration,
  LicenseConfigurationEntry,
  LicenseData,
  LicenseStatus,
  LicensedFeature,
} from '@/licenseManagement/licenseManagement.types';

type LicenseStatusSource = {
  additionalFeatures?: Array<{
    name?: string;
    validity?: string;
    daysToExpiration?: number;
    validThrough?: string;
  }>;
  companyName?: string;
  configuration?: unknown;
  contractNumber?: string;
  daysToExpiration?: number;
  hostname?: string;
  level?: string;
  userCount?: number;
  userLimitRestrict?: number;
  userLimitWarn?: number;
  validThrough?: string;
  validity?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function normalizeLicenseConfigurationEntry(value: unknown): LicenseConfigurationEntry | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const entry: LicenseConfigurationEntry = {};

  for (const [key, fieldValue] of Object.entries(value)) {
    if (typeof fieldValue === 'string' || typeof fieldValue === 'undefined') {
      entry[key] = fieldValue;
    }
  }

  return entry;
}

export function normalizeLicenseConfiguration(
  configuration: LicenseStatusSource['configuration'],
): LicenseConfiguration | undefined {
  if (!isRecord(configuration)) {
    return undefined;
  }

  const normalizedConfiguration: LicenseConfiguration = {};

  for (const [section, value] of Object.entries(configuration)) {
    normalizedConfiguration[section] = normalizeLicenseConfigurationEntry(value);
  }

  return normalizedConfiguration;
}

export function normalizeLicenseData(license?: LicenseStatusSource): LicenseData {
  if (!license) {
    return { additionalFeatures: [] };
  }

  return {
    additionalFeatures: license.additionalFeatures?.map((feature) => ({
      name: feature.name ?? '',
      validity: feature.validity ?? '',
      daysToExpiration: feature.daysToExpiration,
      validThrough: feature.validThrough,
    })),
    companyName: license.companyName,
    configuration: normalizeLicenseConfiguration(license.configuration),
    contractNumber: license.contractNumber,
    daysToExpiration: license.daysToExpiration,
    hostname: license.hostname,
    level: license.level,
    userCount: license.userCount,
    userLimitRestrict: license.userLimitRestrict,
    userLimitWarn: license.userLimitWarn,
    validThrough: license.validThrough,
    validity: license.validity,
  };
}

export function getLicenseStatusFromLicense(license?: LicenseStatusSource): LicenseStatus {
  if (!license) {
    return LICENSE_STATUS.UNKNOWN;
  }

  const cutOffs = license.level === 'Trial' ? WARNING_FLAGS.TRIAL : WARNING_FLAGS.STANDARD;
  const daysToExpiration = license.daysToExpiration ?? 0;

  if (license.validity === ValidityEnum.Valid) {
    if (daysToExpiration > cutOffs.SHOW_ADMIN_WARNING_DAYS_COUNT) {
      return LICENSE_STATUS.VALID;
    }
    if (daysToExpiration > cutOffs.SHOW_WARNING_DAYS_COUNT) {
      return LICENSE_STATUS.SHOW_ADMIN_LICENSE_WARNING;
    }
    return LICENSE_STATUS.SHOW_LICENSE_WARNING;
  }

  if (license.validity === ValidityEnum.Expired) {
    return LICENSE_STATUS.EXPIRED;
  }

  if (license.validity === ValidityEnum.NoLicense) {
    return LICENSE_STATUS.NONE;
  }

  return LICENSE_STATUS.UNKNOWN;
}

export function isFeatureValid(licensedFeature: LicensedFeature, featureName: string): boolean {
  const validity = licensedFeature.validity;
  return licensedFeature.name === featureName && (validity === ValidityEnum.Valid || validity === 'Valid');
}

export function hasValidFeatureInLicense(license: LicenseData, feature: Feature): boolean {
  return (license.additionalFeatures ?? []).some((licensedFeature) => {
    return isFeatureValid(licensedFeature, Feature[feature]);
  });
}

export function hasAssetGroupsInLicense(license: LicenseData): boolean {
  return hasValidFeatureInLicense(license, Feature.Asset_Group);
}

export function getFeatureFromLicense(license: LicenseData, name: string): LicensedFeature | undefined {
  return (license.additionalFeatures ?? []).find(
    (licensedFeature) => licensedFeature.name.toLowerCase() === name.toLowerCase(),
  );
}

export function canAddUsersForLicense(license: LicenseData): boolean {
  const { userLimitRestrict, userCount } = license;
  return !userLimitRestrict || (userCount ?? 0) < userLimitRestrict;
}

export function shouldDisplayLicenseWarning(licenseStatus?: LicenseStatus): boolean {
  return [LICENSE_STATUS.SHOW_ADMIN_LICENSE_WARNING, LICENSE_STATUS.SHOW_LICENSE_WARNING].includes(
    licenseStatus as LicenseStatus,
  );
}
